import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService } from '../../services/adminService';
import {
  ArrowLeft,
  Phone,
  Mail,
  Car,
  Calendar,
  Package,
  CreditCard,
  RefreshCw,
  Loader,
  CheckCircle,
  AlertCircle,
  User,
  MapPin,
  Shield,
} from 'lucide-react';

const fmtDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtDateOnly = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const fmtMoney = (n) =>
  n == null || n === ''
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN')}`;

const formatCard = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '—';
  return digits.replace(/(.{4})/g, '$1 ').trim();
};

const STATUS_CLASS = {
  active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  pending: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  pending_activation: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  expired: 'bg-red-50 text-red-700 ring-red-600/20',
  cancelled: 'bg-slate-100 text-slate-600 ring-slate-600/15',
  past_due: 'bg-orange-50 text-orange-700 ring-orange-600/20',
  premium: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  basic: 'bg-slate-100 text-slate-600 ring-slate-600/15',
};

const StatusPill = ({ value }) => {
  if (!value) return null;
  const key = String(value).toLowerCase();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset ${
        STATUS_CLASS[key] || 'bg-slate-100 text-slate-700 ring-slate-600/15'
      }`}
    >
      {String(value).replace(/_/g, ' ')}
    </span>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4 py-2 border-b border-slate-50 last:border-0">
    <dt className="text-xs font-medium text-slate-500 shrink-0">{label}</dt>
    <dd className="text-sm font-medium text-slate-900 sm:text-right break-all">{value ?? '—'}</dd>
  </div>
);

const emptyPlanForm = {
  planId: '',
  vehicleId: '',
  subscriptionId: '',
  startedAt: '',
  expiresAt: '',
  amountPaid: '',
  note: '',
};

const CustomerDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [planForm, setPlanForm] = useState(emptyPlanForm);
  const [savingPlan, setSavingPlan] = useState(false);
  const [flash, setFlash] = useState(null);
  const [mode, setMode] = useState('assign'); // assign | change

  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['adminCustomerDetail', userId],
    queryFn: async () => {
      const result = await AdminService.getCustomerDetail(userId);
      if (!result.success) throw new Error(result.error || 'Failed to load customer');
      return result;
    },
    enabled: Boolean(userId),
  });

  const { data: plans = [] } = useQuery({
    queryKey: ['membershipPlansForAssign'],
    queryFn: () => AdminService.listMembershipPlansForAssign(),
  });

  const user = data?.user || null;
  const customer = data?.customer || null;
  const roles = data?.roles || [];
  const vehicles = data?.vehicles || [];
  const subscriptions = data?.subscriptions || [];

  const activePlans = useMemo(
    () => (plans || []).filter((p) => p.is_active !== false),
    [plans]
  );

  const currentSub = useMemo(() => {
    return (
      subscriptions.find((s) => ['active', 'pending_activation'].includes(String(s.status || '').toLowerCase())) ||
      subscriptions[0] ||
      null
    );
  }, [subscriptions]);

  const openAssign = () => {
    setMode('assign');
    setFlash(null);
    setPlanForm({
      ...emptyPlanForm,
      planId: activePlans[0]?.id || '',
      vehicleId: vehicles[0]?.id || '',
      startedAt: new Date().toISOString().slice(0, 10),
      amountPaid: activePlans[0]?.price != null ? String(activePlans[0].price) : '',
    });
  };

  const openChange = (sub) => {
    setMode('change');
    setFlash(null);
    setPlanForm({
      planId: sub.plan_id || '',
      vehicleId: sub.vehicle_id || vehicles[0]?.id || '',
      subscriptionId: sub.id,
      startedAt: sub.started_at ? String(sub.started_at).slice(0, 10) : new Date().toISOString().slice(0, 10),
      expiresAt: sub.expires_at ? String(sub.expires_at).slice(0, 10) : '',
      amountPaid: sub.amount_paid != null ? String(sub.amount_paid) : '',
      note: '',
    });
  };

  const onPlanSelect = (planId) => {
    const plan = activePlans.find((p) => p.id === planId);
    setPlanForm((prev) => {
      const started = prev.startedAt || new Date().toISOString().slice(0, 10);
      let expiresAt = prev.expiresAt;
      if (plan?.duration_months && started) {
        const d = new Date(started);
        d.setMonth(d.getMonth() + Number(plan.duration_months || 12));
        expiresAt = d.toISOString().slice(0, 10);
      }
      return {
        ...prev,
        planId,
        amountPaid: plan?.price != null ? String(plan.price) : prev.amountPaid,
        expiresAt,
      };
    });
  };

  const handleSavePlan = async () => {
    if (!planForm.planId) {
      setFlash({ type: 'error', message: 'Plan select karo' });
      return;
    }
    setSavingPlan(true);
    setFlash(null);
    try {
      const result = await AdminService.assignCustomerPlan({
        customerId: userId,
        planId: planForm.planId,
        vehicleId: planForm.vehicleId || null,
        subscriptionId: mode === 'change' ? planForm.subscriptionId || null : null,
        action: 'assign',
        startedAt: planForm.startedAt ? new Date(planForm.startedAt).toISOString() : null,
        expiresAt: planForm.expiresAt ? new Date(`${planForm.expiresAt}T23:59:59`).toISOString() : null,
        amountPaid: planForm.amountPaid !== '' ? Number(planForm.amountPaid) : null,
        note: planForm.note || null,
      });

      if (!result.success) {
        setFlash({ type: 'error', message: result.error || 'Plan update fail' });
        return;
      }

      const planName = result.data?.plan_name || 'Plan';
      setFlash({
        type: 'success',
        message:
          mode === 'change'
            ? `${planName} pe update ho gaya (upgrade/downgrade)`
            : `${planName} assign ho gaya`,
      });
      setPlanForm(emptyPlanForm);
      setMode('assign');
      await queryClient.invalidateQueries({ queryKey: ['adminCustomerDetail', userId] });
      await queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
    } catch (e) {
      setFlash({ type: 'error', message: e.message || 'Plan update fail' });
    } finally {
      setSavingPlan(false);
    }
  };

  const handleCancelSub = async (sub) => {
    if (!window.confirm('Is membership ko cancel karna hai?')) return;
    setSavingPlan(true);
    setFlash(null);
    try {
      const result = await AdminService.cancelCustomerPlan(userId, sub.id);
      if (!result.success) {
        setFlash({ type: 'error', message: result.error || 'Cancel fail' });
        return;
      }
      setFlash({ type: 'success', message: 'Membership cancel ho gayi' });
      await queryClient.invalidateQueries({ queryKey: ['adminCustomerDetail', userId] });
      await queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
    } catch (e) {
      setFlash({ type: 'error', message: e.message || 'Cancel fail' });
    } finally {
      setSavingPlan(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader className="animate-spin text-primary-500" size={28} />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/users')}
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={16} /> Back to Users
        </button>
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
          {error?.message || 'Customer not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => navigate('/users')}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"
          >
            <ArrowLeft size={16} /> Users
          </button>
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-100 to-indigo-100 text-lg font-bold text-primary-700">
              {(user.name || 'C').charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{user.name || 'Customer'}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                {user.phone && (
                  <span className="inline-flex items-center gap-1">
                    <Phone size={14} className="text-slate-400" />
                    {user.phone}
                  </span>
                )}
                {user.email && (
                  <span className="inline-flex items-center gap-1">
                    <Mail size={14} className="text-slate-400" />
                    {user.email}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {roles.map((role) => (
                  <span
                    key={role}
                    className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold capitalize text-slate-700 ring-1 ring-inset ring-slate-600/10"
                  >
                    {role}
                  </span>
                ))}
                <StatusPill value={user.status} />
                {customer?.membership_status && <StatusPill value={customer.membership_status} />}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            type="button"
            onClick={openAssign}
            className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 shadow-sm"
          >
            <Package size={16} />
            Assign Plan
          </button>
        </div>
      </div>

      {flash && (
        <div
          className={`flex items-start gap-3 rounded-xl border p-4 ${
            flash.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          <p className="text-sm font-medium">{flash.message}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="xl:col-span-2 space-y-5">
          {/* Profile */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <User size={18} className="text-primary-500" />
              <h2 className="text-base font-semibold text-slate-900">Profile</h2>
            </div>
            <dl>
              <InfoRow label="Name" value={user.name} />
              <InfoRow label="Phone" value={user.phone} />
              <InfoRow label="Email" value={user.email} />
              <InfoRow label="Account status" value={user.status} />
              <InfoRow label="Created" value={fmtDate(user.created_at)} />
              <InfoRow label="Last login" value={fmtDate(user.last_login)} />
              <InfoRow label="User ID" value={user.id} />
            </dl>
          </section>

          {/* Customer record */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <Shield size={18} className="text-primary-500" />
              <h2 className="text-base font-semibold text-slate-900">Customer record</h2>
            </div>
            {customer ? (
              <dl>
                <InfoRow label="Package" value={customer.package_name || '—'} />
                <InfoRow label="Membership status" value={customer.membership_status || '—'} />
                <InfoRow label="Validity" value={customer.membership_validity || '—'} />
                <InfoRow label="Contact" value={customer.contact_number || user.phone} />
                <InfoRow label="Email (CRM)" value={customer.email_id || user.email} />
                <InfoRow
                  label="Address / State"
                  value={[customer.state, customer.pin_code].filter(Boolean).join(' · ') || '—'}
                />
                <InfoRow label="Car number" value={customer.car_number || '—'} />
                <InfoRow label="Car model" value={customer.car_model || '—'} />
                <InfoRow label="Fuel type" value={customer.fuel_type || '—'} />
                <InfoRow label="COD / Paid" value={fmtMoney(customer.cod_amount)} />
                <InfoRow label="Payment mode" value={customer.payment_mode || '—'} />
                <InfoRow label="Blood group" value={customer.blood_group || '—'} />
              </dl>
            ) : (
              <p className="text-sm text-slate-500">
                Customers table mein record nahi hai — plan assign karte hi ban jayega.
              </p>
            )}
          </section>

          {/* Vehicles */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <Car size={18} className="text-primary-500" />
              <h2 className="text-base font-semibold text-slate-900">
                Vehicles ({vehicles.length})
              </h2>
            </div>
            {vehicles.length === 0 ? (
              <p className="text-sm text-slate-500">Koi vehicle linked nahi hai</p>
            ) : (
              <div className="space-y-3">
                {vehicles.map((v) => (
                  <div
                    key={v.id}
                    className="rounded-lg border border-slate-100 bg-slate-50/80 p-3"
                  >
                    <p className="text-sm font-semibold text-slate-900">
                      {v.registration_number || 'No registration'}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {[v.brand, v.model, v.fuel_type, v.vehicle_type].filter(Boolean).join(' · ') ||
                        '—'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Subscriptions */}
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <CreditCard size={18} className="text-primary-500" />
                <h2 className="text-base font-semibold text-slate-900">
                  Memberships ({subscriptions.length})
                </h2>
              </div>
            </div>

            {subscriptions.length === 0 ? (
              <p className="text-sm text-slate-500 mb-4">Abhi koi membership nahi — neeche plan assign karo</p>
            ) : (
              <div className="space-y-3 mb-2">
                {subscriptions.map((sub) => (
                  <div
                    key={sub.id}
                    className="rounded-xl border border-slate-200 p-4"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-slate-900">
                          {sub.plan_name || 'Plan'}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {sub.plan_slug}
                          {sub.vehicle_registration
                            ? ` · ${sub.vehicle_registration}`
                            : ''}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        <StatusPill value={sub.status} />
                        {sub.payment_status && <StatusPill value={sub.payment_status} />}
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600 sm:grid-cols-4">
                      <div>
                        <p className="text-slate-400">Started</p>
                        <p className="font-medium">{fmtDateOnly(sub.started_at)}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Expires</p>
                        <p className="font-medium">{fmtDateOnly(sub.expires_at)}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Paid</p>
                        <p className="font-medium">{fmtMoney(sub.amount_paid)}</p>
                      </div>
                      <div>
                        <p className="text-slate-400">Card</p>
                        <p className="font-medium tabular-nums">{formatCard(sub.membership_card_number)}</p>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openChange(sub)}
                        className="rounded-lg border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-100"
                      >
                        Upgrade / Downgrade
                      </button>
                      {['active', 'pending_activation', 'pending'].includes(
                        String(sub.status || '').toLowerCase()
                      ) && (
                        <button
                          type="button"
                          onClick={() => handleCancelSub(sub)}
                          disabled={savingPlan}
                          className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Plan manage panel */}
        <div className="space-y-5">
          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sticky top-4">
            <div className="mb-1 flex items-center gap-2">
              <Package size={18} className="text-primary-500" />
              <h2 className="text-base font-semibold text-slate-900">
                {mode === 'change' ? 'Upgrade / Downgrade' : 'Assign plan'}
              </h2>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              {mode === 'change'
                ? 'Current membership ka plan badlo — higher ya lower dono allowed'
                : 'Naya plan assign karo (comp / admin grant)'}
            </p>

            {!planForm.planId && mode === 'assign' ? (
              <button
                type="button"
                onClick={openAssign}
                className="w-full rounded-lg bg-primary-500 py-2.5 text-sm font-semibold text-white hover:bg-primary-600"
              >
                Start assign
              </button>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">Plan *</label>
                  <select
                    value={planForm.planId}
                    onChange={(e) => onPlanSelect(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="">Select plan</option>
                    {activePlans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} — ₹{Number(p.price || 0).toLocaleString('en-IN')}
                        {p.duration_months ? ` · ${p.duration_months} mo` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">Vehicle</label>
                  <select
                    value={planForm.vehicleId}
                    onChange={(e) => setPlanForm((f) => ({ ...f, vehicleId: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                  >
                    <option value="">No vehicle / optional</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.registration_number || v.id.slice(0, 8)}
                        {v.model ? ` · ${v.model}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={12} /> Start
                      </span>
                    </label>
                    <input
                      type="date"
                      value={planForm.startedAt}
                      onChange={(e) => {
                        const startedAt = e.target.value;
                        setPlanForm((f) => {
                          const plan = activePlans.find((p) => p.id === f.planId);
                          let expiresAt = f.expiresAt;
                          if (plan?.duration_months && startedAt) {
                            const d = new Date(startedAt);
                            d.setMonth(d.getMonth() + Number(plan.duration_months || 12));
                            expiresAt = d.toISOString().slice(0, 10);
                          }
                          return { ...f, startedAt, expiresAt };
                        });
                      }}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-slate-600">Expiry</label>
                    <input
                      type="date"
                      value={planForm.expiresAt}
                      onChange={(e) => setPlanForm((f) => ({ ...f, expiresAt: e.target.value }))}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                    Amount paid (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={planForm.amountPaid}
                    onChange={(e) => setPlanForm((f) => ({ ...f, amountPaid: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                    Admin note
                  </label>
                  <textarea
                    rows={2}
                    value={planForm.note}
                    onChange={(e) => setPlanForm((f) => ({ ...f, note: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none"
                    placeholder="Optional reason..."
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setPlanForm(emptyPlanForm);
                      setMode('assign');
                    }}
                    className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    disabled={savingPlan}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={handleSavePlan}
                    disabled={savingPlan}
                    className="flex-[2] inline-flex items-center justify-center gap-2 rounded-lg bg-primary-500 py-2.5 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-50"
                  >
                    {savingPlan ? (
                      <Loader size={16} className="animate-spin" />
                    ) : (
                      <Package size={16} />
                    )}
                    {mode === 'change' ? 'Update plan' : 'Assign plan'}
                  </button>
                </div>
              </div>
            )}

            {currentSub && (
              <div className="mt-5 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
                  Current plan
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {currentSub.plan_name || '—'}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Expires {fmtDateOnly(currentSub.expires_at)} ·{' '}
                  <span className="capitalize">{currentSub.status}</span>
                </p>
              </div>
            )}
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <MapPin size={16} className="text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-900">Quick tips</h3>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-500 list-disc pl-4">
              <li>Assign = naya active membership create</li>
              <li>Upgrade / Downgrade = same subscription pe plan change</li>
              <li>Same vehicle pe purani active membership cancel ho jayegi</li>
              <li>
                Pehle SQL chalao:{' '}
                <code className="text-[10px] bg-slate-100 px-1 rounded">
                  admin-customer-detail-plan.sql
                </code>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
